import { Alert, Button, Card, CardBody } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './dashboard.module.css'
import { AppLayout } from '@/components/AppLayout'
import { Skeleton } from '@/components/Skeleton'
import {
  api,
  type Cancellation,
  type CancellationStats,
  type CancellationStatus,
  type StripeStatus,
} from '@/lib/api'
import { getAuthState, isAuthenticated, type User } from '@/lib/auth-store'
import { formatCurrency, formatPercent, formatRelativeTime } from '@/lib/format'

export const Route = createFileRoute('/dashboard')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: DashboardPage,
})

const STATUS_LABELS: Record<CancellationStatus, string> = {
  active: 'In Progress',
  won_back: 'Recovered',
  lost: 'Lost',
  paused: 'Paused',
}

const STATUS_STYLE_MAP: Record<CancellationStatus, string> = {
  active: 'cancellationStatusActive',
  won_back: 'cancellationStatusWonBack',
  lost: 'cancellationStatusLost',
  paused: 'cancellationStatusPaused',
}

function DashboardPage() {
  const auth = getAuthState()
  const [user] = useState<User | null>(auth.user)
  const [stats, setStats] = useState<CancellationStats | null>(null)
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stripeConnecting, setStripeConnecting] = useState(false)
  const [recentCancellations, setRecentCancellations] = useState<Cancellation[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const [statsResponse, stripeResponse, cancellationsResponse] = await Promise.all([
          api.getCancellationStats().catch(() => null),
          api.getStripeStatus().catch(() => null),
          api.listCancellations({ per_page: 5 }).catch(() => null),
        ])
        if (statsResponse) setStats(statsResponse.data)
        if (stripeResponse) setStripeStatus(stripeResponse.data)
        if (cancellationsResponse) setRecentCancellations(cancellationsResponse.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchData()
  }, [])

  const handleConnectStripe = () => {
    setStripeConnecting(true)
    api
      .getStripeConnectUrl()
      .then((response) => {
        window.location.href = response.data.redirect_url
      })
      .catch(() => {
        setStripeConnecting(false)
      })
  }

  if (isLoading) {
    return (
      <AppLayout title="Dashboard">
        {/* Welcome Section */}
        <Card className={styles.welcomeCard}>
          <CardBody>
            <h2 className={styles.welcomeTitle}>
              Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
            </h2>
            <p className={styles.welcomeSubtitle}>
              Here&apos;s an overview of your win-back performance.
            </p>
          </CardBody>
        </Card>

        {/* Stats Grid Loading */}
        <div className={styles.statsGrid}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className={styles.statCard}>
              <CardBody>
                <Skeleton width="3rem" height="2rem" />
                <Skeleton width="80%" height="1rem" />
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Activity Loading */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Recent Activity</h3>
          </div>
          <Card>
            <CardBody>
              <Skeleton width="100%" height="1rem" />
              <Skeleton width="80%" height="1rem" />
              <Skeleton width="60%" height="1rem" />
            </CardBody>
          </Card>
        </div>
      </AppLayout>
    )
  }

  if (error) {
    return (
      <AppLayout title="Dashboard">
        <Card className={styles.welcomeCard}>
          <CardBody>
            <h2 className={styles.welcomeTitle}>
              Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
            </h2>
          </CardBody>
        </Card>
        <Alert variant="danger">{error}</Alert>
      </AppLayout>
    )
  }

  const showStripeConnect = stripeStatus && !stripeStatus.connected

  return (
    <AppLayout title="Dashboard">
      {/* Welcome Section */}
      <Card className={styles.welcomeCard}>
        <CardBody>
          <h2 className={styles.welcomeTitle}>
            Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
          </h2>
          <p className={styles.welcomeSubtitle}>
            Here&apos;s an overview of your win-back performance.
          </p>
        </CardBody>
      </Card>

      {/* Stripe Connection Banner */}
      {showStripeConnect && (
        <Card className={styles.stripeBanner}>
          <CardBody>
            <div className={styles.stripeBannerContent}>
              <div className={styles.stripeBannerText}>
                <h3 className={styles.stripeBannerTitle}>Connect Stripe to get started</h3>
                <p className={styles.stripeBannerDescription}>
                  BounceBack needs access to your Stripe account to detect cancellations and trigger
                  win-back sequences automatically.
                </p>
              </div>
              <Button variant="primary" onClick={handleConnectStripe} loading={stripeConnecting}>
                Connect Stripe
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <Card className={`${styles.statCard} ${styles.statRecovered}`}>
          <CardBody>
            <div className={styles.statIcon}>💰</div>
            <div className={styles.statValue}>
              {stats ? formatCurrency(stats.total_mrr_recovered, stats.currency) : '$0'}
            </div>
            <div className={styles.statLabel}>Recovered MRR</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.statWinBack}`}>
          <CardBody>
            <div className={styles.statIcon}>🎯</div>
            <div className={styles.statValue}>
              {stats ? formatPercent(stats.win_back_rate) : '0%'}
            </div>
            <div className={styles.statLabel}>Win-Back Rate</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statIcon}>🚫</div>
            <div className={styles.statValue}>{stats?.total_cancellations ?? 0}</div>
            <div className={styles.statLabel}>Cancellations</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statIcon}>📧</div>
            <div className={styles.statValue}>{stats?.total_wins ?? 0}</div>
            <div className={styles.statLabel}>Active Sequences</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statIcon}>📬</div>
            <div className={styles.statValue}>{stats?.emails_sent ?? 0}</div>
            <div className={styles.statLabel}>Emails Sent</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statIcon}>📋</div>
            <div className={styles.statValue}>{stats ? formatPercent(stats.open_rate) : '0%'}</div>
            <div className={styles.statLabel}>Open Rate</div>
          </CardBody>
        </Card>
      </div>

      {/* Empty state when no cancellations */}
      {stats && stats.total_cancellations === 0 ? (
        <Card className={styles.emptyState}>
          <CardBody>
            <div className={styles.emptyIcon}>🔄</div>
            <h3 className={styles.emptyTitle}>No cancellations yet</h3>
            <p className={styles.emptyDescription}>
              {stripeStatus?.connected
                ? 'When a subscriber cancels, BounceBack will automatically start your win-back sequence. Check back once you receive your first cancellation.'
                : 'Connect your Stripe account to start detecting cancellations and winning back subscribers automatically.'}
            </p>
            {!stripeStatus?.connected && (
              <Button variant="primary" onClick={handleConnectStripe} loading={stripeConnecting}>
                Connect Stripe
              </Button>
            )}
          </CardBody>
        </Card>
      ) : (
        /* Recent Activity */
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Recent Activity</h3>
          </div>
          <Card>
            <CardBody>
              <div className={styles.activityList}>
                <div className={styles.activityItem}>
                  <span className={styles.activityIcon}>📊</span>
                  <div className={styles.activityContent}>
                    <span className={styles.activityText}>
                      {stats?.total_wins ?? 0} subscribers won back out of{' '}
                      {stats?.total_cancellations ?? 0} cancellations
                    </span>
                    <span className={styles.activityMeta}>
                      {stats ? formatCurrency(stats.total_mrr_lost, stats.currency) : '$0'} MRR
                      lost,{' '}
                      {stats ? formatCurrency(stats.total_mrr_recovered, stats.currency) : '$0'}{' '}
                      recovered
                    </span>
                  </div>
                </div>
                {stats && stats.emails_sent > 0 && (
                  <div className={styles.activityItem}>
                    <span className={styles.activityIcon}>📬</span>
                    <div className={styles.activityContent}>
                      <span className={styles.activityText}>
                        {stats.emails_sent} win-back emails sent
                      </span>
                      <span className={styles.activityMeta}>
                        {formatPercent(stats.open_rate)} open rate,{' '}
                        {formatPercent(stats.click_rate)} click rate
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Recent Cancellations */}
          {recentCancellations.length > 0 && (
            <>
              <div className={styles.sectionHeader} style={{ marginTop: '1.5rem' }}>
                <h3 className={styles.sectionTitle}>Recent Cancellations</h3>
                <Link to="/cancellations" className={styles.viewAll}>
                  View All
                </Link>
              </div>
              <Card>
                <CardBody>
                  <div className={styles.cancellationsList}>
                    {recentCancellations.map((cancellation) => {
                      const statusStyleKey = STATUS_STYLE_MAP[cancellation.status]
                      const statusStyleClass = statusStyleKey ? (styles[statusStyleKey] ?? '') : ''
                      return (
                        <Link
                          key={cancellation.id}
                          to="/cancellations/$id"
                          params={{ id: cancellation.id }}
                          className={styles.cancellationItem}
                        >
                          <div className={styles.cancellationCustomer}>
                            <span className={styles.cancellationCustomerName}>
                              {cancellation.customerName ?? 'Unknown'}
                            </span>
                            <span className={styles.cancellationCustomerEmail}>
                              {cancellation.customerEmail}
                            </span>
                          </div>
                          <span className={styles.cancellationProduct}>
                            {cancellation.productName ?? '--'}
                          </span>
                          <span className={styles.cancellationMrr}>
                            {formatCurrency(cancellation.mrr, cancellation.currency)}
                          </span>
                          <span className={`${styles.cancellationStatus} ${statusStyleClass}`}>
                            {STATUS_LABELS[cancellation.status]}
                          </span>
                          <span className={styles.cancellationDate}>
                            {formatRelativeTime(cancellation.cancelledAt)}
                          </span>
                        </Link>
                      )
                    })}
                  </div>
                </CardBody>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Quick Tips */}
      <Card className={styles.tipsCard}>
        <CardBody>
          <h3 className={styles.tipsTitle}>Tips for better win-back rates</h3>
          <ul className={styles.tipsList}>
            <li>Send your first win-back email within 1 hour of cancellation for best results</li>
            <li>Offer a discount or incentive in at least one email in your sequence</li>
            <li>Include a cancellation survey to understand why customers leave</li>
            <li>A/B test your subject lines and CTAs to optimize conversion</li>
          </ul>
        </CardBody>
      </Card>
    </AppLayout>
  )
}
